/**
 * Image Optimization Utilities for Cloudflare Images
 * 
 * Uses Cloudflare flexible variants for automatic format (WebP/AVIF) and quality optimization.
 * Requires flexible variants to be enabled in Cloudflare Images dashboard.
 */

// Check if Cloudflare Workers optimization is enabled (legacy)
const CF_WORKERS_ENABLED = import.meta.env.VITE_CF_WORKERS_ENABLED === 'true';
const CF_WORKERS_URL = import.meta.env.VITE_CF_WORKERS_URL || '';

export interface ImageOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'auto' | 'webp' | 'avif' | 'jpeg';
}

/**
 * Get the best available image URL (Cloudflare Images > Cloudflare Workers > Supabase)
 */
export function getBestImageUrl(
  supabaseUrl: string,
  cloudflareUrl?: string | null,
  options: ImageOptions = {}
): string {
  // Prefer Cloudflare Images URL if available
  if (cloudflareUrl) {
    return getCloudflareImagesVariant(cloudflareUrl, options);
  }

  // Fall back to Cloudflare Workers if enabled
  if (CF_WORKERS_ENABLED && CF_WORKERS_URL) {
    return getOptimizedImageUrl(supabaseUrl, options);
  }

  // Default to Supabase URL
  return supabaseUrl;
}

/**
 * Get Cloudflare Images URL with flexible variant
 * 
 * Cloudflare Images URLs: https://imagedelivery.net/{account_hash}/{image_id}/{variant}
 * Flexible variant format: w=800,h=600,fit=scale-down,q=85,f=auto
 */
export function getCloudflareImagesVariant(
  cloudflareUrl: string,
  options: ImageOptions = {}
): string {
  const baseUrl = cloudflareUrl.replace(/\/[^\/]*$/, '');
  
  // Build flexible variant parameters for optimization
  const params: string[] = [];
  
  if (options.width) params.push(`w=${options.width}`);
  if (options.height) params.push(`h=${options.height}`);
  params.push('fit=scale-down'); // Never crop
  params.push(`q=${options.quality || 85}`); // Default 85% quality
  params.push('f=auto'); // Auto WebP/AVIF
  
  return `${baseUrl}/${params.join(',')}`;
}

/**
 * Get responsive hero image URL based on viewport
 */
export function getResponsiveHeroUrl(
  cloudflareUrl: string,
  isMobile: boolean = false
): string {
  // Optimize hero images: mobile gets smaller, desktop gets full width
  const width = isMobile ? 960 : 1920;
  return getCloudflareImagesVariant(cloudflareUrl, { width, quality: 85 });
}

/**
 * Generate optimized image URL using Cloudflare Workers (legacy)
 */
export function getOptimizedImageUrl(
  supabaseUrl: string,
  options: ImageOptions = {}
): string {
  // If Cloudflare Workers is not enabled, return original URL
  if (!CF_WORKERS_ENABLED || !CF_WORKERS_URL) {
    return supabaseUrl;
  }

  // Extract image path from Supabase URL
  const supabasePattern = /\/storage\/v1\/object\/public\/portfolio-images\/(.*)/;
  const match = supabaseUrl.match(supabasePattern);
  
  if (!match) {
    return supabaseUrl; // Return original if pattern doesn't match
  }

  const imagePath = match[1];

  // Build Cloudflare Workers URL with optimization parameters
  const params = new URLSearchParams();
  
  if (options.width) params.set('width', options.width.toString());
  if (options.height) params.set('height', options.height.toString());
  params.set('quality', (options.quality || 85).toString());
  params.set('format', 'auto'); // Serve AVIF when supported

  const queryString = params.toString();
  const cfUrl = `${CF_WORKERS_URL}/cdn-images/${imagePath}`;
  
  return queryString ? `${cfUrl}?${queryString}` : cfUrl;
}

/**
 * Generate responsive srcset for different screen sizes
 */
export function generateResponsiveSrcSet(
  supabaseUrl: string,
  cloudflareUrl?: string | null,
  sizes: number[] = [640, 960, 1280, 1920]
): string {
  // Use Cloudflare Images with flexible variants
  if (cloudflareUrl) {
    return sizes
      .map(width => {
        const url = getCloudflareImagesVariant(cloudflareUrl, { width, quality: 85 });
        return `${url} ${width}w`;
      })
      .join(', ');
  }

  // Fall back to Cloudflare Workers if enabled
  if (CF_WORKERS_ENABLED && CF_WORKERS_URL) {
    return sizes
      .map(width => {
        const url = getOptimizedImageUrl(supabaseUrl, { 
          width, 
          quality: 85
        });
        return `${url} ${width}w`;
      })
      .join(', ');
  }

  // Return original URL if no optimization available
  return supabaseUrl;
}

/**
 * Get sizes attribute for responsive images
 */
export function getImageSizes(
  breakpoints: { maxWidth: string; size: string }[] = [
    { maxWidth: '640px', size: '100vw' },
    { maxWidth: '768px', size: '50vw' },
    { maxWidth: '1024px', size: '33vw' },
    { maxWidth: '1280px', size: '25vw' },
  ]
): string {
  const sizeString = breakpoints
    .map(bp => `(max-width: ${bp.maxWidth}) ${bp.size}`)
    .join(', ');
  
  return `${sizeString}, 400px`;
}

/**
 * Detect if browser supports modern image formats
 */
export function detectSupportedFormat(): 'avif' | 'webp' | 'jpeg' {
  if (typeof window === 'undefined') return 'jpeg';
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  // Check AVIF support
  if (canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0) {
    return 'avif';
  }
  
  // Check WebP support
  if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
    return 'webp';
  }
  
  return 'jpeg';
}